import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { userDevicesState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { formatRelative } from '@/util/dates';
import type { UserId } from '@rock/types';
import type { FC } from 'react';

export const UserDevicesTab: FC<{ userId: UserId }> = ({ userId }) => {
    const [devices, devicesLoadable] = useLoadableAtomValue(userDevicesState(userId), []);

    return (
        <Card>
            <CardHeader>
                <CardTitle>Devices</CardTitle>
                <CardDescription>Registered devices, most recently active first.</CardDescription>
            </CardHeader>
            <CardContent>
                {devices.length === 0 ? (
                    <div className="section-description">
                        {devicesLoadable.state === 'loading'
                            ? 'Loading devices…'
                            : devicesLoadable.state === 'hasError'
                              ? 'Couldn’t load devices.'
                              : 'No devices registered.'}
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Device</TableHead>
                                <TableHead>Platform</TableHead>
                                <TableHead>App version</TableHead>
                                <TableHead>Push</TableHead>
                                <TableHead className="text-right">Last active</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {devices.map((device) => (
                                <TableRow key={device.id}>
                                    <TableCell className="font-medium">
                                        <div className="flex flex-col">
                                            <span>{device.name || '—'}</span>
                                            <span className="text-xs text-muted-foreground">{device.id}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {device.platform}
                                        {device.platformVersion && (
                                            <span className="text-muted-foreground"> {device.platformVersion}</span>
                                        )}
                                    </TableCell>
                                    <TableCell>{device.appVersion ?? '—'}</TableCell>
                                    <TableCell>
                                        {device.pushToken ? (
                                            <Badge variant="default">Enabled</Badge>
                                        ) : (
                                            <Badge variant="outline">Off</Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                        {formatRelative(device.updatedAt)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};
